import { useState, useEffect, useMemo } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import { faLocationDot, faStar, faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { getCurrentLocation, getDistanceFromLocation } from "../../utils/geolocation";
import Chip from "../../sub/components/Chip";
import { platform } from "../../platform";

const iconSize = platform === "web" ? "sm" : 12;

/**
 * Returns a summary text for a list of reviews.
 *
 * If there are no reviews, returns "No reviews yet".
 * Otherwise, returns the average rating (rounded to one decimal place)
 * and the total number of reviews in the format: "avg (count reviews)".
 *
 * @param {Array<{ rating: number }>} reviews - Array of review objects with a rating property.
 * @returns {string} Summary text for the reviews.
 */
const setReviewsText = (reviews) => {
  if (reviews && reviews.rating) {
    return `${reviews.rating} ${Array.isArray(reviews.reviews) && `(${reviews.reviews.length} reviews)`}`;
  } else {
    return "No reviews yet";
  }
};

/**
 * Returns the rental status text and corresponding icon based on the rental availability.
 *
 * @param {boolean} isRental - Indicates if the item is available for rental.
 * @returns {{ text: string, icon: IconDefinition }} An object containing the status text and icon.
 */
const getRentalStatus = (isRental) => {
  return {
    text: `${isRental ? "" : "Not "}Available for Rental`,
    icon: isRental ? faCircleCheck : faCircleXmark,
  };
};

/**
 * Returns the adoption status of a park, including descriptive text and an icon.
 *
 * @param {boolean} isAdopted - Indicates if the park has already been adopted.
 * @param {boolean} isAdoptionEligable - Indicates if the park is eligible for adoption.
 * @returns {{ text: string, icon: IconDefinition }} An object containing the status text and corresponding icon.
 */
const getAdoptionStatus = (isAdopted, isAdoptionEligable) => {
  if (isAdopted) {
    return {
      text: "Adopted",
      icon: faCircleCheck,
    };
  }

  return {
    text: `${isAdoptionEligable ? "" : "Not "}Eligible for Adoption`,
    icon: isAdoptionEligable ? faCircleCheck : faCircleXmark,
  };
};

const ParkDetailsChips = ({ parkData, googleReviews, onPress }) => {
  const [rentalStatus, setRentalStatus] = useState(getRentalStatus(false));
  const [adoptionStatus, setAdoptionStatus] = useState(getAdoptionStatus(false, false));
  const [distance, setDistance] = useState(null);

  useEffect(() => {
    if (parkData === null) {
      return;
    }

    setRentalStatus((prev) =>
      prev.text !== getRentalStatus(parkData.isRental).text ? getRentalStatus(parkData.isRental) : prev
    );

    setAdoptionStatus((prev) =>
      prev.text !== getAdoptionStatus(parkData.isAdopted, parkData.isAdoptionEligable).text
        ? getAdoptionStatus(parkData.isAdopted, parkData.isAdoptionEligable)
        : prev
    );
  }, [parkData]);

  useEffect(() => {
    const getDistance = async () => {
      if (!parkData) {
        return;
      } else {
        try {
          const userLocation = await getCurrentLocation();
          setDistance(
            await getDistanceFromLocation(userLocation, { lat: parkData.latlng[0], lng: parkData.latlng[1] })
          );
        } catch (err) {
          return null;
        }
      }
    };

    getDistance();
  }, [parkData]);

  const reviewStats = useMemo(() => setReviewsText(googleReviews), [googleReviews]);

  return (
    <>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.chipsContainer}>
          {googleReviews != null && googleReviews?.rating != null && (
            <Chip
              icon={faStar}
              size={iconSize}
              title={reviewStats}
              accessible={true}
              accessibilityLabel={`Average user rating ${reviewStats}`}
              accessibilityRole="text"
              borderColor={"secondary"}
              backgroundColor={"secondaryVariant"}
              iconColor={"secondary"}
              type="button"
              onPress={onPress.scrollToReviews}
            />
          )}
          {distance !== null && (
            <Chip
              icon={faLocationDot}
              size={iconSize}
              title={`${distance !== null && `${distance} miles away`}`}
              accessible={true}
              accessibilityLabel={`${distance} miles away to ${parkData.title}`}
              accessibilityRole="text"
              borderColor="tertiary"
              backgroundColor="tertiaryVariant"
              iconColor="tertiary"
              type="button"
              onPress={() => onPress.openMaps(parkData.latlng[0], parkData.latlng[1])}
            />
          )}
        </View>
      </ScrollView>

      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={styles.chipsContainer}>
          {parkData.isRental && (
            <Chip
              icon={rentalStatus.icon}
              size={iconSize}
              title={rentalStatus.text}
              accessible={true}
              accessibilityLabel={rentalStatus.text}
              accessibilityRole="text"
              borderColor="quaternary"
              backgroundColor="quaternaryVariant"
              iconColor="quaternary"
              type="button"
              onPress={onPress.openRentals}
            />
          )}
          {parkData.isAdoptionEligable && !parkData.isAdopted && (
            <Chip
              icon={adoptionStatus.icon}
              size={iconSize}
              title={adoptionStatus.text}
              accessible={true}
              accessibilityLabel={adoptionStatus.text}
              accessibilityRole="text"
              borderColor="secondary"
              backgroundColor="secondaryVariant"
              iconColor="secondary"
              type="button"
              onPress={onPress.openAdoption}
            />
          )}
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  chipsContainer: {
    flexDirection: "row",
    paddingVertical: 5,
    gap: 10,
  },
});

export default ParkDetailsChips;
